import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Building2,
  Users,
  ShieldCheck,
  BarChart3,
  Globe,
  ArrowRight,
  CheckCircle,
  Star,
  MapPin
} from 'lucide-react';
import Logo from '../components/Logo';

const features = [
  {
    icon: Globe,
    title: 'Your Global Brand',
    description: 'Instantly launch a professional, branded property portal that showcases your unique expertise.'
  },
  {
    icon: Building2,
    title: 'Elite Portfolio',
    description: 'Manage an unlimited collection of premium listings with rich media, floor plans, and immersive details.'
  },
  {
    icon: Users,
    title: 'Direct Engagement',
    description: 'Connect directly with serious buyers. Cut out the noise and own your lead generation.'
  },
  {
    icon: BarChart3,
    title: 'Precision Analytics',
    description: 'Make data-driven decisions with real-time performance tracking and enquiry intelligence.'
  },
  {
    icon: ShieldCheck,
    title: 'RERA Compliance',
    description: 'Elevate your credibility with built-in RERA verification badges for every listing.'
  },
  {
    icon: Star,
    title: 'Luxury UX',
    description: 'A cutting-edge, mobile-first experience that positions your agency as a market leader.'
  }
];

const testimonials = [
  {
    name: 'Rajesh Properties',
    role: 'CEO, Luxury Segment',
    content: 'EstateCloud redefined our digital presence. We saw a 40% surge in high-intent enquiries within weeks.',
    rating: 5
  },
  {
    name: 'Sunrise Developers',
    role: 'Managing Director',
    content: 'The most intuitive platform for serious developers. No distractions, just results.',
    rating: 5
  }
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FDFEFF]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-navy-50/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Logo className="w-9 h-9" textClassName="text-xl" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/search" className="text-navy-700 font-medium hover:text-sky-500 transition-colors">
              Search
            </Link>
            <Link to="/login" className="text-navy-700 font-medium hover:text-sky-500 transition-colors">
              Agency Login
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/register">
              <Button className="bg-navy-900 hover:bg-navy-800 text-white shadow-lg shadow-navy-200 transition-all duration-300 hover:scale-105 active:scale-95 px-6" data-testid="get-started-nav-btn">
                Join Today
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-24 px-6 relative overflow-hidden">
        {/* Abstract Background Decoration */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[800px] h-[800px] bg-sky-50/50 rounded-full blur-[120px] -z-10" />
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-[600px] h-[600px] bg-navy-50/50 rounded-full blur-[100px] -z-10" />

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <span className="inline-flex items-center px-4 py-1.5 bg-sky-50 text-sky-600 rounded-full text-xs font-bold uppercase tracking-wider mb-8 border border-sky-100 italic">
                Tomorrow's Real Estate Technology
              </span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold text-navy-900 leading-[1.1] mb-8">
                The New Standard <br />
                <span className="text-sky-500 italic">Of Luxury Space.</span>
              </h1>
              <p className="text-xl text-navy-600/80 mb-10 max-w-xl leading-relaxed font-light">
                Architecting a seamless bridge between developers and premium clients. Your legacy deserves a digital stage that matches your ambition.
              </p>
              <div className="flex flex-col sm:flex-row gap-5">
                <Link to="/register">
                  <Button size="lg" className="bg-navy-900 hover:bg-navy-800 text-white h-14 px-10 text-lg shadow-xl shadow-navy-100 transition-all rounded-full group" data-testid="hero-get-started-btn">
                    Get Started Free
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/search">
                  <Button size="lg" variant="outline" className="h-14 px-10 text-lg border-navy-200 hover:bg-navy-50 rounded-full" data-testid="view-demo-btn">
                    Browse Properties
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-8 mt-12">
                {[
                  { label: "Direct Access", color: "text-green-600" },
                  { label: "RERA Verified", color: "text-green-600" },
                  { label: "Zero Commission", color: "text-green-600" }
                ].map((item, idx) => (
                  <span key={idx} className="flex items-center gap-2 text-sm font-medium text-navy-500">
                    <CheckCircle className={`w-4 h-4 ${item.color}`} />
                    {item.label}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_40px_80px_-15px_rgba(15,23,42,0.15)] ring-1 ring-white/50 backdrop-blur-sm">
                <img
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=85&w=1200"
                  alt="Modern architectural masterpiece"
                  className="w-full h-[550px] object-cover hover:scale-105 transition-transform duration-[2s]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 via-transparent to-transparent" />
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="glass p-6 rounded-2xl border border-white/40 shadow-2xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-navy-500 font-bold mb-1 italic">Featured Development</p>
                        <h3 className="text-2xl font-heading font-medium text-navy-900">Elysian Towers</h3>
                        <p className="text-navy-500 text-sm flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> Worli, Mumbai
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-navy-900 tracking-tight">₹12.5 Cr+</p>
                        <Badge className="bg-green-500/10 text-green-600 border-none px-3 py-1 mt-2">Verified Agency</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Float Cards */}
              <div className="absolute -top-10 -right-10 glass p-5 rounded-2xl shadow-2xl border border-white/50 hidden xl:flex items-center gap-4 animate-bounce-slow">
                <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <p className="font-bold text-navy-900 text-sm leading-none mb-1">Authentic Listings</p>
                  <p className="text-navy-500 text-[11px] uppercase tracking-wider">100% RERA Verified</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bento Grid Features */}
      <section className="py-32 px-6 bg-white relative">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-20">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold text-navy-900 mb-6">
              Empowering Growth. <br />
              <span className="text-sky-500 italic">Without Compromise.</span>
            </h2>
            <p className="text-lg text-navy-500 leading-relaxed font-light">
              We provide the digital infrastructure for modern real estate pioneers. From branding to closing, one unified ecosystem.
            </p>
          </div>

          <div className="grid md:grid-cols-6 gap-6 stagger-children">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className={`
                  bg-[#F8FAFC] p-8 rounded-[2rem] border border-navy-50/50 hover:bg-white hover:shadow-2xl hover:border-sky-100 transition-all duration-500
                  ${index === 0 || index === 3 ? 'md:col-span-4' : 'md:col-span-2'}
                `}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-white shadow-sm ring-1 ring-navy-50 rounded-2xl flex items-center justify-center mb-10 group-hover:bg-sky-50 transition-colors">
                  <feature.icon className="w-7 h-7 text-sky-600" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-navy-900 mb-4">{feature.title}</h3>
                <p className="text-navy-500 leading-relaxed text-sm font-light max-w-md">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-navy-900" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sky-400 font-medium text-sm tracking-widest uppercase">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mt-4 mb-4">
              Get Started in 3 Simple Steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Register Your Agency', desc: 'Sign up with your company details and RERA number for instant verification.' },
              { step: '02', title: 'Add Your Properties', desc: 'Upload properties with images, layouts, and all details. Customize your website.' },
              { step: '03', title: 'Share & Receive Enquiries', desc: 'Share your unique website link. Receive client enquiries directly.' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`step-${index + 1}`}
              >
                <div className="text-6xl font-heading font-bold text-sky-500/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-navy-300">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sky-500 font-medium text-sm tracking-widest uppercase">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy-900 mt-4">
              Trusted by Leading Agencies
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-navy-100 rounded-xl p-6"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-navy-700 mb-4 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-navy-900">{testimonial.name}</p>
                  <p className="text-sm text-navy-500">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-navy-900 to-navy-800" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
            Ready to Grow Your Real Estate Business?
          </h2>
          <p className="text-navy-300 mb-8 text-lg">
            Join hundreds of agencies already using EstateCloud to showcase their properties.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 h-12 px-8 text-base" data-testid="cta-get-started-btn">
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-navy-950" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-sky-500" />
              <span className="text-lg font-heading font-bold text-white">EstateCloud</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-navy-400">
              <Link to="/terms" className="hover:text-sky-400 transition-colors">Terms & Conditions</Link>
              <Link to="/privacy" className="hover:text-sky-400 transition-colors">Privacy Policy</Link>
              <span>© 2026 EstateCloud. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
